import React, { useState, useEffect } from 'react';
import { cartService } from '../../services/cartService';
import { ordersApi, CartItem } from '../../services/api';
import SuccessModal from '../common/SuccessModal';
import './Cart.css';

interface CartProps {
    onClose: () => void;
    initialCheckout?: boolean; // If true, show checkout form immediately
}

const Cart: React.FC<CartProps> = ({ onClose, initialCheckout = false }) => {
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [showCheckout, setShowCheckout] = useState(initialCheckout);
    const [checkoutForm, setCheckoutForm] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        paymentMode: 'EMAIL',
    });
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadCart();

        // Listen for cart updates
        const handleCartUpdate = () => {
            loadCart();
        };
        window.addEventListener('cartUpdated', handleCartUpdate);

        return () => {
            window.removeEventListener('cartUpdated', handleCartUpdate);
        };
    }, []);

    const loadCart = () => {
        setCartItems(cartService.getCartItems());
    };

    const updateQuantity = (productId: string, quantity: number, selectedColor?: string, selectedSize?: string) => {
        cartService.updateQuantity(productId, quantity, selectedColor, selectedSize);
        loadCart();
    };

    const removeItem = (productId: string, selectedColor?: string, selectedSize?: string) => {
        cartService.removeFromCart(productId, selectedColor, selectedSize);
        loadCart();
    };

    const handleCheckout = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkoutForm.name || !checkoutForm.phone) {
            setError('Please fill in all required fields');
            return;
        }

        setError(null);
        setSubmitting(true);
        try {
            // Create or get user first
            // For simplicity, we'll use phone as userId
            const userId = `user_${checkoutForm.phone.replace(/\D/g, '')}`;

            // Prepare order data
            const orderData = {
                userId: userId,
                userEmail: checkoutForm.email || undefined,
                products: cartItems.map(item => ({
                    productId: item.productId,
                    quantity: item.quantity,
                    price: item.product.price,
                    selectedColor: item.selectedColor,
                    selectedSize: item.selectedSize,
                })),
                paymentDate: new Date().toISOString().split('T')[0],
                paymentMode: checkoutForm.paymentMode,
                shippingAddress: checkoutForm.address,
                shippingCharges: shippingCharges,
                totalAmount: finalTotal,
                status: 'pending',
            };

            await ordersApi.create(orderData);

            // Clear cart after successful order
            cartService.clearCart();
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error placing order:', error);
            setError('Failed to place order. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const totalAmount = cartService.getCartTotal();
    const shippingCharges = 50; // Fixed shipping charge (can be made configurable)
    const finalTotal = totalAmount + shippingCharges;

    if (cartItems.length === 0 && !showCheckout) {
        return (
            <div className="cart-overlay" onClick={onClose}>
                <div className="cart-container" onClick={(e) => e.stopPropagation()}>
                    <div className="cart-header">
                        <h2>Shopping Cart</h2>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="cart-empty">
                        <p>Your cart is empty</p>
                        <button className="btn btn-primary" onClick={onClose}>Continue Shopping</button>
                    </div>
                </div>
            </div>
        );
    }

    if (showCheckout) {
        return (
            <>
            <div className="cart-overlay" onClick={onClose}>
                <div className="cart-container checkout-container" onClick={(e) => e.stopPropagation()}>
                    <div className="cart-header">
                        <h2>Checkout</h2>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <form onSubmit={handleCheckout} className="checkout-form">
                            {error && (
                                <div className="error-message" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
                                    {error}
                                </div>
                            )}
                        <div className="form-group">
                            <label htmlFor="name">Name *</label>
                            <input
                                type="text"
                                id="name"
                                value={checkoutForm.name}
                                onChange={(e) => setCheckoutForm(prev => ({ ...prev, name: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                value={checkoutForm.phone}
                                onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={checkoutForm.email}
                                onChange={(e) => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="address">Shipping Address</label>
                            <textarea
                                id="address"
                                value={checkoutForm.address}
                                onChange={(e) => setCheckoutForm(prev => ({ ...prev, address: e.target.value }))}
                                rows={3}
                            />
                        </div>
                        <div className="checkout-total">
                            <div className="checkout-summary">
                                <div className="checkout-summary-row checkout-total-row">
                                    <strong>Total:</strong>
                                    <strong>₹{totalAmount.toFixed(2)}</strong>
                                </div>
                                <div className="checkout-summary-row">
                                    <strong>+ Shipping Charges</strong>
                                </div>
                            </div>
                        </div>
                        <div className="checkout-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowCheckout(false)}>
                                Back to Cart
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                {submitting ? 'Placing Order...' : 'Place Order'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
                <SuccessModal
                    isOpen={showSuccessModal}
                    onClose={() => {
                        setShowSuccessModal(false);
                        onClose();
                    }}
                    type="order"
                />
            </>
        );
    }

    return (
        <>
        <div className="cart-overlay" onClick={onClose}>
            <div className="cart-container" onClick={(e) => e.stopPropagation()}>
                <div className="cart-header">
                    <h2>Shopping Cart ({cartService.getCartCount()} items)</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="cart-items">
                    {cartItems.map((item, index) => (
                        <div key={index} className="cart-item">
                            <div className="cart-item-image">
                                <img
                                    src={item.product.mainImage || (item.product.images && item.product.images[0]) || ''}
                                    alt={item.product.name}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="cart-item-details">
                                <h4>{item.product.name}</h4>
                                {item.selectedColor && (
                                    <p className="cart-item-attr">Color: {item.selectedColor}</p>
                                )}
                                {item.selectedSize && (
                                    <p className="cart-item-attr">Size: {item.selectedSize}</p>
                                )}
                                <div className="cart-item-price">
                                    <span>₹{item.product.price} × {item.quantity}</span>
                                    <strong>₹{(item.product.price * item.quantity).toFixed(2)}</strong>
                                </div>
                            </div>
                            <div className="cart-item-actions">
                                <div className="quantity-controls">
                                    <button
                                        onClick={() => updateQuantity(item.productId, item.quantity - 1, item.selectedColor, item.selectedSize)}
                                    >
                                        −
                                    </button>
                                    <span>{item.quantity}</span>
                                    <button
                                        onClick={() => updateQuantity(item.productId, item.quantity + 1, item.selectedColor, item.selectedSize)}
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    className="remove-btn"
                                    onClick={() => removeItem(item.productId, item.selectedColor, item.selectedSize)}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="cart-footer">
                    <div className="cart-total">
                        <strong>Total: ₹{totalAmount.toFixed(2)}</strong>
                    </div>
                    <button className="btn btn-primary checkout-btn" onClick={() => setShowCheckout(true)}>
                        Proceed to Checkout
                    </button>
                </div>
            </div>
        </div>
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    onClose();
                }}
                type="order"
            />
        </>
    );
};

export default Cart;

